import React from "react";
import { useEffect, useRef, type ReactNode } from "react";

import { InfoMessage } from "@amphore-dev/amphore-lib";

import { cn } from "@/utils/cn";

interface InfiniteListProps<T> {
	pages: T[][];
	renderItem: (item: T, index: number) => ReactNode;
	keyExtractor: (item: T) => string;
	fetchNextPage: () => void;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	fetchPreviousPage?: () => void;
	hasPreviousPage?: boolean;
	isFetchingPreviousPage?: boolean;
	loadPreviousMessage?: ReactNode;
	isLoading?: boolean;
	skeleton?: ReactNode;
	noDataMessage?: ReactNode;
	endOfListMessage?: ReactNode;
	className?: string;
	itemClassName?: string;
	wrapper?: (children: ReactNode) => ReactNode;
	error?: string | null;
}

export function InfiniteList<T>({
	pages,
	renderItem,
	keyExtractor,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	fetchPreviousPage,
	hasPreviousPage,
	isFetchingPreviousPage,
	loadPreviousMessage,
	isLoading,
	skeleton,
	noDataMessage,
	endOfListMessage,
	className,
	itemClassName,
	wrapper,
	error,
}: InfiniteListProps<T>) {
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!sentinelRef.current) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (
					entry.isIntersecting &&
					hasNextPage &&
					!isFetchingNextPage
				) {
					fetchNextPage();
				}
			},
			{ threshold: 0.1 }
		);

		observer.observe(sentinelRef.current);
		return () => observer.disconnect();
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	if (isLoading) {
		return <div className={className}>{skeleton}</div>;
	}

	const items = pages.flat();

	if (items.length === 0 && noDataMessage) {
		return <div className={className}>{noDataMessage}</div>;
	}

	const content = error ? (
		<InfoMessage color="danger">{error}</InfoMessage>
	) : (
		<>
			{isFetchingPreviousPage && skeleton}
			{!isFetchingPreviousPage &&
				hasPreviousPage &&
				loadPreviousMessage && (
					<button
						className="w-full py-2 text-center text-sm text-neutral-500 transition-colors hover:text-neutral-300"
						onClick={fetchPreviousPage}
					>
						{loadPreviousMessage}
					</button>
				)}

			{pages.map((page, pageIndex) =>
				page.map((item, itemIndex) => (
					<div
						key={keyExtractor(item)}
						className={cn(["max-w-full", itemClassName])}
					>
						{renderItem(item, pageIndex * page.length + itemIndex)}
					</div>
				))
			)}

			<div ref={sentinelRef} className="col-span-full h-1 w-full" />

			{isFetchingNextPage && skeleton}
			{!hasNextPage && !isFetchingNextPage && endOfListMessage}
		</>
	);

	if (wrapper) {
		return <>{wrapper(content)}</>;
	}

	return <div className={className}>{content}</div>;
}
